import { CmUiTableActionData, CmUiTableColumn, CmUiTableHeaderButton } from '@cm/ui-modules';

/** Access key of the admin page and its actions (`acccess.config.ts`). */
export const SYSTEM_UPDATES_MANAGE_ACCESS_KEY = 'system-updates.manage';

export const SYSTEM_UPDATES_STORAGE_KEYS = {
  /** Admin table page size. The list has no sort (the API orders it), so there is no orderBy. */
  ADMIN_PAGE_SIZE: 'system-updates-admin-page-size',
  /** "What's new" archive table page size. No sort there either (newest first, from the API). */
  ARCHIVE_PAGE_SIZE: 'system-updates-archive-page-size',
  /** `uuid` list of updates whose panel the user closed in this browser, to skip a re-show on reload. */
  PANEL_DISMISSED: 'system-updates-panel-dismissed',
} as const;

export const SYSTEM_UPDATES_DEFAULT_PAGE_SIZE = 25;

export const SYSTEM_UPDATES_ADMIN_COLUMNS: CmUiTableColumn[] = [
  {
    prop: 'title',
    label: 'ADMIN.systemUpdates.table.title',
    sortable: false,
    visible: true,
    searchable: false,
    isCustomField: false,
    widthBehavior: 'grow',
    ownLayout: true,
  },
  {
    prop: 'published',
    label: 'ADMIN.systemUpdates.table.status',
    sortable: false,
    visible: true,
    searchable: false,
    isCustomField: false,
    widthBehavior: 'shrink',
  },
  {
    // "Everyone" or the role codes (cell template in the admin page)
    prop: 'audienceRoles',
    label: 'ADMIN.systemUpdates.table.audience',
    sortable: false,
    visible: true,
    searchable: false,
    isCustomField: false,
    widthBehavior: 'shrink',
    ownLayout: true,
  },
  {
    prop: 'readCount',
    label: 'ADMIN.systemUpdates.table.readCount',
    sortable: false,
    visible: true,
    searchable: false,
    isCustomField: false,
    widthBehavior: 'shrink',
  },
  {
    prop: 'publishedAt',
    label: 'ADMIN.systemUpdates.table.publishedAt',
    sortable: false,
    visible: true,
    searchable: false,
    isCustomField: false,
    widthBehavior: 'shrink',
  },
  {
    prop: 'createdAt',
    label: 'ADMIN.systemUpdates.table.createdAt',
    sortable: false,
    visible: true,
    searchable: false,
    isCustomField: false,
    widthBehavior: 'shrink',
  },
];

export const SYSTEM_UPDATES_ADMIN_ROW_ACTIONS: CmUiTableActionData[] = [
  { type: 'button', key: 'edit', name: 'common.edit' },
  {
    type: 'button',
    key: 'publish',
    name: 'ADMIN.systemUpdates.action.publish',
    hideConditions: [{ prop: 'published', value: true }],
  },
  {
    type: 'button',
    key: 'unpublish',
    name: 'ADMIN.systemUpdates.action.unpublish',
    hideConditions: [{ prop: 'published', value: false }],
  },
  { type: 'button', key: 'viewers', name: 'ADMIN.systemUpdates.action.viewers' },
  { type: 'button', key: 'delete', name: 'common.delete' },
];

export const SYSTEM_UPDATES_ADMIN_HEADER_BUTTONS: CmUiTableHeaderButton[] = [
  { action: 'create', icon: 'add', label: 'ADMIN.systemUpdates.action.create', visible: true },
];

export const SYSTEM_UPDATE_VIEWERS_COLUMNS: CmUiTableColumn[] = [
  {
    prop: 'name',
    label: 'ADMIN.systemUpdates.viewers.user',
    sortable: false,
    visible: true,
    searchable: false,
    isCustomField: false,
    widthBehavior: 'grow',
    ownLayout: true,
  },
  {
    prop: 'email',
    label: 'ADMIN.systemUpdates.viewers.email',
    sortable: false,
    visible: true,
    searchable: false,
    isCustomField: false,
    widthBehavior: 'grow',
    ownLayout: true,
  },
  {
    prop: 'readAt',
    label: 'ADMIN.systemUpdates.viewers.readAt',
    sortable: false,
    visible: true,
    searchable: false,
    isCustomField: false,
    widthBehavior: 'shrink',
  },
];

/** "What's new" archive (every reader): newest first, a row opens the viewer dialog. */
export const SYSTEM_UPDATES_ARCHIVE_COLUMNS: CmUiTableColumn[] = [
  {
    prop: 'title',
    label: 'ADMIN.systemUpdates.table.title',
    sortable: false,
    visible: true,
    searchable: false,
    isCustomField: false,
    widthBehavior: 'grow',
    ownLayout: true,
  },
  {
    prop: 'publishedAt',
    label: 'ADMIN.systemUpdates.table.publishedAt',
    sortable: false,
    visible: true,
    searchable: false,
    isCustomField: false,
    widthBehavior: 'shrink',
  },
  {
    // "New" / "Read" status chip (cell template in the page)
    prop: 'read',
    label: 'ADMIN.systemUpdates.table.status',
    sortable: false,
    visible: true,
    searchable: false,
    isCustomField: false,
    widthBehavior: 'shrink',
  },
  {
    // a video / image icon, empty for words only (`SystemUpdateArchiveRow.media`)
    prop: 'media',
    label: 'ADMIN.systemUpdates.table.media',
    sortable: false,
    visible: true,
    searchable: false,
    isCustomField: false,
    widthBehavior: 'shrink',
  },
];

/**
 * Archive toolbar, in priority order (the table moves the last ones into ⋯ first). Both are shown
 * only while something is unread — the page sets `visible` / `disabled`.
 */
export const SYSTEM_UPDATES_ARCHIVE_HEADER_BUTTONS: CmUiTableHeaderButton[] = [
  { action: 'watchNew', icon: 'play_circle', label: 'ADMIN.systemUpdates.page.watchNew', visible: false },
  { action: 'markAllRead', icon: 'done_all', label: 'ADMIN.systemUpdates.page.markAllRead', visible: false },
];
